/**
 * CB-11.2: the ANONYMOUS client path to a contract document URL.
 *
 * CB-11.1 made every in-app contract read resolve through a signed URL. That works
 * for signed-in users: a tenant-scoped storage RLS policy
 * (supabase/migrations/20260804l_contracts_storage_rls.sql) lets the browser client
 * sign for itself.
 *
 * Two surfaces cannot do that, because neither has a Supabase session at all:
 *
 *   /customer/:projectId  the portal authenticates with a `customer_token` in
 *                         localStorage, validated by the Python backend. It never
 *                         calls supabase.auth, so its client is role `anon`.
 *   /sign?token=          an emailed signing link. No account, no session.
 *
 * `anon` has no tenant, so no tenant-scoped policy can serve them, and the obvious
 * alternative — mirroring the existing `contracts_select_anonymous_with_token` row
 * policy into storage — would let ANY anonymous caller read every contract PDF that
 * has a signature_token. That is the exposure CB-11.2 closes, not a fix for it.
 *
 * So these two surfaces ask the backend, which authorises the credential against the
 * contract row and returns a service_role-signed URL.
 *
 * There is deliberately no fallback to the stored value. A fallback would work today
 * (the stored value is a permanent public URL) and would therefore hide its own
 * breakage right up until the bucket flips — which is precisely how SignContractPage
 * was still rendering a raw public URL long after CB-11.1 claimed to have wired
 * every consumer.
 */

const BACKEND_URL =
  import.meta.env.VITE_PYTHON_BACKEND_URL || 'https://admin-api.joulescrm.com';

export const PUBLIC_CONTRACT_DOCUMENT_ENDPOINT = '/api/v1/contracts/public/document-url';

/**
 * How an anonymous caller proves it may see a contract document.
 *
 * A signing link identifies ONE contract, so the token is the whole credential and
 * the caller never names a contract. A portal session covers a project, so it must
 * name the contract — and the backend constrains that to the session's project.
 */
export type PublicContractCredential =
  | { signatureToken: string }
  | { customerToken: string; projectId: string; contractId: string };

type RequestBody =
  | { signature_token: string }
  | { customer_token: string; project_id: string; contract_id: string };

/**
 * Returns null both when the caller may not see the document and when there is no
 * document to see. Callers surface that as "couldn't load"; they must not fall back
 * to the stored value.
 */
function toRequestBody(credential: PublicContractCredential): RequestBody | null {
  if ('signatureToken' in credential) {
    const token = credential.signatureToken?.trim();
    return token ? { signature_token: token } : null;
  }

  const customerToken = credential.customerToken?.trim();
  const projectId = credential.projectId?.trim();
  const contractId = credential.contractId?.trim();
  if (!customerToken || !projectId || !contractId) return null;

  return { customer_token: customerToken, project_id: projectId, contract_id: contractId };
}

export async function publicContractDocumentUrl(
  credential: PublicContractCredential,
): Promise<string | null> {
  const body = toRequestBody(credential);
  // An incomplete credential is refused here rather than sent, so a half-populated
  // portal session cannot become a request the backend has to reason about.
  if (!body) return null;

  try {
    const response = await fetch(`${BACKEND_URL}${PUBLIC_CONTRACT_DOCUMENT_ENDPOINT}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    });

    if (!response.ok) return null;

    const data = await response.json();
    if (!data?.available || !data?.url) return null;
    return data.url as string;
  } catch (err) {
    console.error('[publicContractDocumentUrl] failed to resolve', err);
    return null;
  }
}

/**
 * Open an anonymous caller's contract document in a new tab. Returns false if there
 * was nothing to open, so the caller can show its own message.
 *
 * The tab is opened BEFORE the await and navigated afterwards: resolving the URL is a
 * round trip, and window.open() after an await has lost its user-gesture context and
 * gets blocked.
 *
 * NO `noopener` on the pre-opened tab. Passing it makes window.open() return null by
 * design — the browser severs the handle — leaving nothing to navigate, a blank tab,
 * and a second tab from the fallback. `tab.opener = null` after navigating gives the
 * same protection. (Same rule as openContractDocument; it was a real bug once.)
 */
export async function openPublicContractDocument(
  credential: PublicContractCredential,
): Promise<boolean> {
  const tab = window.open('', '_blank');
  const url = await publicContractDocumentUrl(credential);

  if (!url) {
    tab?.close();
    return false;
  }

  if (tab) {
    tab.opener = null;
    tab.location.href = url;
  } else {
    // Popup blocked outright; nothing was opened, so a direct open is all that's left.
    window.open(url, '_blank', 'noopener,noreferrer');
  }
  return true;
}
